import { defineStore } from 'pinia'
import i18n from '@/i18n'
import type {
  AllocationSupplier,
  SupplierContact,
  SupplierLocation,
  SupplierSpecialization,
  SupplierType,
} from '@/types/supplierType'
import { RoundRobinSupplierCheck } from '@/types/productType'
import type { SortBy } from '@/types/types'
import SupplierService from '@/services/SupplierService'
import { isAuthorized } from '@/utils/isAuthorized'
import { RolePermission } from '@/types/roleType'

export const useSupplierStore = defineStore('suppliers', {
  state: (): {
    data: Map<string, SupplierType & { timestamp?: number, outdated?: boolean }>
    page: number
    filters?: Record<string, any>
    sortBy?: Record<string, 'ASC' | 'DESC'>

    allocationSuppliers: Map<string, AllocationSupplier>
    allocationParams?: string
    allSuppliers: Map<string, SupplierType>
  } => ({
    data: new Map(),
    page: 1,
    filters: undefined,
    sortBy: {
      name: 'ASC',
    },

    allocationSuppliers: new Map(),
    allocationParams: undefined,
    allSuppliers: new Map<string, SupplierType>(),
  }),
  actions: {
    /**
     * Suppliers
     */
    async addNewSupplier(supplier: Omit<SupplierType, 'id'>) {
      return (await SupplierService.postSupplier(supplier)).data
    },

    async fetchNextPage(clear?: boolean) {
      if (clear) this.page = 1

      return SupplierService.getSuppliers({
        page: this.page++,
        filters: this.filters,
        order: this.sortBy,
      })
        .then((res) => {
          if (clear) this.data.clear()
          return res
        })
        .then(({ data }) => data.forEach((x) => this.data.set(x.id, x)))
    },

    async setFilters(filters: Record<string, any> = {}) {
      if (JSON.stringify(filters) !== JSON.stringify(this.filters)) {
        this.filters = { ...filters }

        await this.fetchNextPage(true)
      }
    },

    async setSorting(sortBy?: SortBy) {
      if (sortBy !== this.sortBy) {
        this.sortBy = sortBy ? { [sortBy.field]: sortBy.direction } : undefined

        await this.fetchNextPage(true)
      }
    },

    async getAllSuppliers() {
      if (isAuthorized(RolePermission.SUPPLIER_READ)) {
        if (this.allSuppliers.size) {
          return Array.from<SupplierType>(this.allSuppliers.values())
        }

        const { data } = await SupplierService.getSuppliers({
          pagination: false,
        })

        if (data) {
          data.forEach((x) => this.allSuppliers.set(x.id, x))
        }

        return data
      }

      return []
    },

    async fetchSupplierById(id: string, cached = true) {
      const local = this.data.get(id)

      if (
        !cached ||
        !local ||
        !(local?.timestamp ?? 0 + 3600000 >= new Date().getTime()) ||
        local?.outdated
      ) {
        const { data } = await SupplierService.getSupplierById(id)

        this.data.set(id, {
          ...(local?.outdated ? local : {}),
          ...data,
          timestamp: new Date().getTime(),
          outdated: false,
        })

        return data
      }

      return local
    },

    async getAllocationSuppliers(
      municipalityId: string,
      startDate: string,
      productId: string,
      isAllocationRequest: boolean,
      clientId?: string,
      endDate?: string,
    ) {
      if (
        this.allocationSuppliers &&
        this.allocationParams ===
          municipalityId + startDate + productId + isAllocationRequest
      ) {
        return Promise.resolve(this.allocationSuppliersAsArray)
      }

      const { data } = await SupplierService.getAllocationSuppliersByProduct(
        municipalityId,
        productId,
        startDate,
        isAllocationRequest,
        clientId,
        endDate,
      )

      this.allocationSuppliers.clear()

      data.forEach((x) => {
        if (x.agbCode === RoundRobinSupplierCheck.AGB) {
          x.name = i18n.global.t('entities.product.roundRobin.use')
          x.roundRobin = true
        }

        this.allocationSuppliers.set(x.id, x)
      })

      this.allocationParams =
        municipalityId + startDate + productId + isAllocationRequest

      return data
    },

    async updateSupplier(supplier: SupplierType) {
      const { data } = await SupplierService.putSupplier(supplier)

      this.data.set(supplier.id, data)

      return data
    },

    async markSupplierAsOutdated(id: string, watching: boolean) {
      const local = this.data.get(id)

      if (local?.timestamp) {
        local.outdated = true

        if (watching) this.fetchSupplierById(id, false)
      }
    },

    async addNewSupplierContact(
      supplierId: string,
      contact: Omit<SupplierContact, 'id'>,
    ) {
      const { data } = await SupplierService.postSupplierContact(
        supplierId,
        contact,
      )

      const current = this.data.get(supplierId)

      if (current)
        this.data.set(supplierId, {
          ...current,
          contacts: [...(current.contacts ?? []), data],
        })

      return data
    },

    /**
     * Contact
     */
    async updateSupplierContact(supplierId: string, contact: SupplierContact) {
      const { data } = await SupplierService.putSupplierContact(
        supplierId,
        contact,
      )

      const current = this.data.get(supplierId)

      if (current)
        this.data.set(supplierId, {
          ...current,
          contacts: current.contacts.map((c) => (c.id === data.id ? data : c)),
        })

      return data
    },

    async removeSupplierContact(supplierId: string, supplierContactId: string) {
      await SupplierService.deleteSupplierContact(supplierId, supplierContactId)

      const current = this.data.get(supplierId)

      if (current)
        this.data.set(supplierId, {
          ...current,
          contacts: current.contacts.filter((c) => c.id !== supplierContactId),
        })
    },

    /**
     * Location
     */
    async addNewSupplierLocation(
      supplierId: string,
      location: Omit<SupplierLocation, 'id'>,
    ) {
      const { data } = await SupplierService.postSupplierLocation(
        supplierId,
        location,
      )

      const current = this.data.get(supplierId)

      if (current)
        this.data.set(supplierId, {
          ...current,
          locations: [...(current.locations ?? []), data],
        })

      return data
    },

    async updateSupplierLocation(
      supplierId: string,
      location: SupplierLocation,
    ) {
      const { data } = await SupplierService.putSupplierLocation(
        supplierId,
        location,
      )

      const current = this.data.get(supplierId)

      if (current)
        this.data.set(supplierId, {
          ...current,
          locations: current.locations.map((l) =>
            l.id === data.id ? data : l,
          ),
        })

      return data
    },

    async removeSupplierLocation(
      supplierId: string,
      supplierLocationId: string,
    ) {
      await SupplierService.deleteSupplierLocation(
        supplierId,
        supplierLocationId,
      )

      const current = this.data.get(supplierId)

      if (current)
        this.data.set(supplierId, {
          ...current,
          locations: current.locations.filter(
            (c) => c.id !== supplierLocationId,
          ),
        })
    },

    /**
     * Specialization
     */
    async addNewSupplierSpecialization(
      supplierId: string,
      specialization: Omit<SupplierSpecialization, 'id'>,
    ) {
      const { data } = await SupplierService.postSupplierSpecialization(
        supplierId,
        specialization,
      )

      const current = this.data.get(supplierId)

      if (current)
        this.data.set(supplierId, {
          ...current,
          specializations: [...(current.specializations ?? []), data],
        })

      return data
    },

    async updateSupplierSpecialization(
      supplierId: string,
      specialization: SupplierSpecialization,
    ) {
      const { data } = await SupplierService.putSupplierSpecialization(
        supplierId,
        specialization,
      )

      const current = this.data.get(supplierId)

      if (current)
        this.data.set(supplierId, {
          ...current,
          specializations: current.specializations.map((e) =>
            e.id === data.id ? data : e,
          ),
        })

      return data
    },

    async removeSupplierSpecialization(
      supplierId: string,
      supplierSpecializationId: string,
    ) {
      await SupplierService.deleteSupplierSpecialization(
        supplierId,
        supplierSpecializationId,
      )

      const current = this.data.get(supplierId)

      if (current)
        this.data.set(supplierId, {
          ...current,
          specializations: current.specializations.filter(
            (c) => c.id !== supplierSpecializationId,
          ),
        })
    },
  },
  getters: {
    dataAsArray: (state) => Array.from<SupplierType>(state.data.values()),

    allocationSuppliersAsArray: (state) =>
      Array.from<AllocationSupplier>(state.allocationSuppliers.values()),

    findOrThrowSupplierById: (state) => {
      return (id: string) => {
        const supplier = state.data.get(id)

        if (!supplier) throw new Error()

        return supplier
      }
    },

    findOrThrowSupplierContactById: (state) => {
      return (supplierId: string, contactId: string) => {
        const contact = state.data
          .get(supplierId)
          ?.contacts.find((x) => x.id === contactId)

        if (!contact) throw new Error()

        return contact
      }
    },

    findOrThrowSupplierLocationById: (state) => {
      return (supplierId: string, locationId: string) => {
        const location = state.data
          .get(supplierId)
          ?.locations.find((x) => x.id === locationId)

        if (!location) throw new Error()

        return location
      }
    },

    findOrThrowSupplierSpecializationById: (state) => {
      return (supplierId: string, specializationId: string) => {
        const specialization = state.data
          .get(supplierId)
          ?.specializations.find((x) => x.id === specializationId)

        if (!specialization) throw new Error()

        return specialization
      }
    },
  },
})
